import React, { useState, useEffect, useCallback } from 'react';
import { Layout } from 'antd';
import type { MenuProps } from 'antd';
import styles from './index.module.less';
import { UserInfo } from '@/models/perms_type';
import { deepClone } from '@/utils/tools';
import Header from './Header'
import PermsNavMenu from './PermsNavMenu'
import ProductNavMenu from './ProductNavMenu'
import { FundProjectionScreenOutlined, SafetyCertificateOutlined, ControlOutlined, ProfileOutlined, DashboardOutlined, BarChartOutlined, AppstoreOutlined, SettingOutlined } from '@ant-design/icons';

const { Content } = Layout;
type MenuItem = Required<MenuProps>['items'][number];
const permsNavMenu: MenuItem[] = [
  {
    key: '/perms',
    label: '权限中心',
    icon: <SafetyCertificateOutlined />,
    children: [
      { key: '/perms/group', label: '租户管理' },
      { key: '/perms/user', label: '人员管理' },
      { key: '/perms/role', label: '角色管理' },
    ],
  },
];

const productNavMenu: MenuItem[] = [
  {
    key: '/resource', icon: <AppstoreOutlined />, label: '资源管理', children: [
      { key: '/resource/vehicles', label: '车辆管理', },
      { key: '/resource/vehicle-teams', label: '车队管理', },
      { key: '/resource/tos-management', label: 'TOS管理', },
      { key: '/resource/ground-staff', label: '地勤管理', },
      { key: '/resource/operation-plan', label: '运营计划管理', },
    ],
  },
  {
    key: '/odd', icon: <FundProjectionScreenOutlined />, label: 'ODD管理', children: [
      { key: '/odd/point', label: '点位管理', },
      // { key: '/odd/d2d', label: 'D2D管理', },
      { key: '/odd/area', label: '运营区域管理', },
      { key: '/odd/area/avoidance', label: '避让路线管理', },
      { key: '/odd/operation-route', label: '运营路线管理', },
      {
        key: '/odd/speed-limit-route',
        label: '限速路线管理',
        children: [
          { key: '/odd/speed-limit-route/config', label: '配置列表' },
          { key: '/odd/speed-limit-route/group', label: '分组管理' },
          { key: '/odd/speed-limit-route/export', label: '导出记录' },
        ],
      },
    ],
  },
  {
    key: '/order', icon: <ProfileOutlined />, label: '调度订单', children: [
      { key: '/order/orders', label: '订单管理', },
      { key: '/order/dispatch-categories', label: '调度管理', },
    ],
  },
  {
    key: '/operation', icon: <BarChartOutlined />, label: '运营管理', children: [
      { key: '/operation/customer-management', label: '客户管理', },
      { key: '/operation/pricing-rules', label: '计价规则', },
      { key: '/order/billing', label: '账单管理', },
    ],
  },
  {
    key: '/ticket', icon: <ControlOutlined />, label: '工单处置', children: [
      { key: '/ticket/tickets', label: '工单管理', },
      { key: '/ticket/anomaly', label: '异常配置', },
      { key: '/ticket/service-desk', label: '客服工作台', },
    ],
  },
  {
    key: '/analysis', icon: <DashboardOutlined />, label: 'BI监控', children: [
      { key: '/analysis/taxi-monitor', label: '多车监控', },
      { key: '/analysis/multi-monitor', label: '多车监控', },
      { key: '/analysis/vehicle-live', label: '车辆直播', },
      { key: '/analysis/dlv-trend', label: '运营统计', },
      { key: '/analysis/taxi-trend', label: '业务报表' }
    ],
  },
  {
    key: 'config', icon: <SettingOutlined />, label: '配置管理', children: [
      { key: '/config/groups', label: '分组管理', },
      { key: '/config/cancel-reasons', label: '订单取消原因配置', },
    ],
  }
];
let allAppPaths = deepClone(productNavMenu).map(item => {
  if (item && 'children' in item && Array.isArray(item.children)) {
    return item.children.flatMap(child => {
      if (!child) return [];

      const paths: string[] = [];

      // 添加二级菜单的 key（确保是 string 类型）
      if (child.key && typeof child.key === 'string') {
        paths.push(child.key);
      }

      // 处理三级菜单（如限速路线管理的子菜单）
      if ('children' in child && Array.isArray(child.children)) {
        child.children.forEach(grandChild => {
          if (grandChild?.key && typeof grandChild.key === 'string') {
            paths.push(grandChild.key);
          }
        });
      }

      return paths;
    });
  }
  return [];
}).flat().filter((item): item is string => typeof item === 'string').concat(
  "/analysis/single-monitor",
  "/odd/point-create",
  "/odd/point-details",
  "/devops/toscase",
  "/devops/tosseat",
  "/order/vehicles",
);
allAppPaths = allAppPaths.map(i => "/taxi" + i).concat(allAppPaths.map(i => "/dlv" + i));

// 生成页面标题映射
const generatePageTitleMap = () => {
  const titleMap: Record<string, string> = {};
  // 添加权限中心的路由标题
  permsNavMenu.forEach(item => {
    if (item && 'children' in item && Array.isArray(item.children)) {
      item.children.forEach(child => {
        if (child && 'key' in child && 'label' in child) {
          titleMap[child.key as string] = `${child.label} - 权限中心`;
        }
      });
    }
  });

  // 添加产品菜单的路由标题（支持 taxi/dlv/truck 前缀）
  ['taxi', 'dlv', 'truck'].forEach(prefix => {
    productNavMenu.forEach(item => {
      if (item && 'children' in item && Array.isArray(item.children)) {
        item.children.forEach(child => {
          if (child && 'key' in child && 'label' in child) {
            const productLabel = prefix === 'taxi' ? 'Robotaxi' : prefix === 'dlv' ? 'Robovan' : 'Robotruck';
            titleMap[`/${prefix}${child.key as string}`] = `${child.label} - ${productLabel}`;
          }
        });
      }
    });
  });

  return titleMap;
};

const pageTitleMap = generatePageTitleMap();
interface HeaderLayoutProps {
  children: React.ReactNode;
  pathname: string;
  userInfo: UserInfo;
  handleLogout: () => void;
  handleJumpTo: (path: string) => void;
}

const HeaderLayout: React.FC<HeaderLayoutProps> = ({
  children,
  pathname,
  userInfo,
  handleLogout,
  handleJumpTo
}) => {
  const [isNavMenuCollapsed, setIsNavMenuCollapsed] = useState(false);
  const [activeMenu, setActiveMenu] = useState('');
  const [isSystemAdmin, setIsSystemAdmin] = useState(false);
  const [isGroupAdmin, setIsGroupAdmin] = useState(false);
  const [isSetActiveProductNavMenu, setIsSetActiveProductNavMenu] = useState(false);
  const [activeProductNavMenu, setActiveProductNavMenu] = useState<MenuItem[]>([]);
  const [activePermsNavMenu, setActivePermsNavMenu] = useState<MenuItem[]>([]);


  const handleActivePermsMenu = () => {
    const isSystemAdmin = !!userInfo.roleMap["sr:platform:admin"]
    const isGroupAdmin = !!userInfo.roleMap["sr:platform:groupAdmin"] || !!userInfo.roleMap["sr:platform:globalDataObserver"];
    setIsSystemAdmin(isSystemAdmin);
    setIsGroupAdmin(isGroupAdmin);
    const activePermsNavMenuList = deepClone(permsNavMenu).map((item: MenuItem) => {
      if (item && 'children' in item && Array.isArray(item.children)) {
        item.children = item.children.filter((child) => {
          if (child && 'key' in child) {
            if (child.key == "/perms/group" && isSystemAdmin) {
              return true;
            }
            if (child.key == "/perms/role" && (isSystemAdmin || isGroupAdmin)) {
              return true;
            }
            if (child.key == "/perms/user" && (isSystemAdmin || isGroupAdmin)) {
              return true;
            }
          }
          return false;
        });
        if (item.children.length > 0) {
          return item;
        }
      }
      return null;
    }).filter(item => item !== null && item !== undefined) as MenuItem[];

    setActivePermsNavMenu(activePermsNavMenuList);
  };
  const handleActiveMenu = useCallback((activeMenuItem: string) => {
    setActiveMenu(activeMenuItem);
    //跳转页面设置
    if (activeMenuItem === 'taxi' || activeMenuItem === 'dlv') {
      const pagePaths = userInfo.pagePaths;

      const activeProductNavMenuList = deepClone(productNavMenu).map((item: MenuItem) => {
        if (item && 'children' in item && Array.isArray(item.children)) {
          item.children = item.children.map((child) => {
            if (child) {
              child.key = `/${activeMenuItem}${child.key}`;
              // 处理三级菜单（如限速路线管理的子菜单）
              if ('children' in child && Array.isArray(child.children)) {
                child.children = child.children.map((grandChild) => {
                  if (grandChild) {
                    grandChild.key = `/${activeMenuItem}${grandChild.key}`;
                  }
                  return grandChild;
                });
              }
            }
            return child;
          });
        } else if (item) {
          item.key = `/${activeMenuItem}${item.key}`;
        }
        return item;
      }).filter(item => {
        // 运营管理模块仅 Robovan 产品线（dlv/truck）显示
        if (item && 'key' in item && item.key === '/operation' && activeMenuItem === 'taxi') {
          return false;
        }

        if (item && 'children' in item && Array.isArray(item.children)) {
          item.children = item.children.filter((child) => {
            if (!child || !('key' in child) || typeof child.key !== 'string') {
              return false;
            }

            // 限速路线管理仅 Robovan 产品线（dlv/truck）显示
            if (child.key.endsWith('/odd/speed-limit-route') && activeMenuItem === 'taxi') {
              return false;
            }

            // 客服工作台仅 Robovan 产品线（dlv/truck）显示
            if (child.key.endsWith('/ticket/service-desk') && activeMenuItem === 'taxi') {
              return false;
            }

            // 处理三级菜单（如限速路线管理的子菜单）
            if ('children' in child && Array.isArray(child.children)) {
              child.children = child.children.filter((grandChild) => {
                return grandChild && 'key' in grandChild && typeof grandChild.key === 'string' && pagePaths.includes(grandChild.key);
              });
              // 如果三级菜单有子项，保留该二级菜单
              return child.children.length > 0;
            }

            return pagePaths.includes(child.key);
          });
          return item.children.length > 0;
        }
        return item && 'key' in item && typeof item.key === 'string' && pagePaths.includes(item.key);
      });
      setIsSetActiveProductNavMenu(true);
      setActiveProductNavMenu(activeProductNavMenuList);
    }
  }, []);

  useEffect(() => {
    handleActivePermsMenu();
  }, [userInfo]);

  useEffect(() => {
    const pathSnippets = pathname.split('/').filter(i => i);
    if (pathSnippets.length > 0) {
      handleActiveMenu(pathSnippets[0]);
    } else {
      // 根路径 '/' 时重置 activeMenu，避免从 perms 后退回来仍然保留权限中心菜单
      setActiveMenu('');
    }

  }, [pathname]);

  useEffect(() => {
    if (!userInfo.userId || !isSetActiveProductNavMenu) {
      return;
    }
    const { pagePaths } = userInfo;
    const isPublicPage = ['/', '/login', '/login-success'].includes(pathname);
    // 如果是公共页面, 允许访问
    if (isPublicPage) {
      return;
    }
    if (pathname.startsWith('/perms/')) {
      const hasPermsSubRouteAccess = pagePaths.some(p => p.startsWith('/perms/'));
      if (hasPermsSubRouteAccess) {
        return;
      }
    }
    if (pathname == "/taxi" || pathname == "/dlv") {
      let targetPath = pathname;
      for (const item of activeProductNavMenu) {
        if (item && 'children' in item && Array.isArray(item.children)) {
          for (const child of item.children) {
            if (child && 'key' in child && pagePaths.includes(child.key as string)) {
              targetPath = child.key as string;
              break;
            }
          }
          if (targetPath !== pathname) {
            break;
          }
        }
      }
      if (targetPath !== pathname) {
        handleJumpTo(targetPath);
        return
      }
    }
    console.log(allAppPaths)
    // TODO: 临时注释权限检查，用于测试订单取消原因配置页面
    // 正式上线前需要联系 @Ryan Zheng 在后端添加权限：/dlv/config/cancel-reasons
    // if (allAppPaths.includes(pathname) && !pagePaths.includes(pathname)) {
    //   handleJumpTo('/exception/403');
    //   return;
    // }
    if (!allAppPaths.includes(pathname)) {
      handleJumpTo('/exception/404');
      return;
    }
  }, [pathname, userInfo.userId, activeProductNavMenu]);

  // 根据路径动态设置页面标题
  useEffect(() => {
    const title = pageTitleMap[pathname];
    if (title) {
      document.title = title;
    } else {
      // 如果没有匹配到，使用默认标题
      document.title = 'Robo运营中台';
    }
  }, [pathname]);
  return (
    <Layout className={styles.layoutContainer}>
      <Header
        handleJumpTo={handleJumpTo}
        handleLogout={handleLogout}
        pathname={pathname}
        handleActiveMenu={handleActiveMenu}
        activeMenu={activeMenu}
        isSystemAdmin={isSystemAdmin}
        isGroupAdmin={isGroupAdmin}
        userInfo={userInfo}
      />
      {activeMenu === 'perms' ? (
        <div className={styles.contentHaveLeftNav}>
          <PermsNavMenu
            userInfo={userInfo}
            pathname={pathname}
            collapsed={isNavMenuCollapsed}
            handleJumpTo={handleJumpTo}
            permsNavMenu={activePermsNavMenu}
            onCollapse={setIsNavMenuCollapsed} />
          <div className={`${styles.content} ${pathname.startsWith('/perms') ? styles.permsPage : ''}`}>
            {children}
          </div>
        </div>
      ) : !!~['taxi', 'dlv', 'truck'].includes(activeMenu) ? (
        <div className={styles.contentHaveLeftNav}>
          <ProductNavMenu
            userInfo={userInfo}
            pathname={pathname}
            collapsed={isNavMenuCollapsed}
            handleJumpTo={handleJumpTo}
            productNavMenu={activeProductNavMenu}
            activeMenu={activeMenu}
            onCollapse={setIsNavMenuCollapsed}
          />
          <div className={`${styles.content} ${pathname.includes('/analysis/multi-monitor') || pathname.includes('/analysis/single-monitor')  ? styles.appPage : ''}`}>
            {children}
          </div>
        </div>
      ) : (
        <Content className={styles.contentNoLeftNav}>
          {children}
        </Content>
      )}
    </Layout>
  );
}

export default HeaderLayout;
